/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Apr 16 2024
 **/

import { useChat } from "@/frameworks/core/API";

export type ChannelItem = WebServices.Chat.Messages.Data | undefined;

export default function useChatMessageActions() {
	const chatAPI = useChat();

	/**
	 * 메시지 전송
	 */
	const sendMessage = async (params: WebServices.Chat.SendMessage.Request) => {
		return (await chatAPI.sendMessage.execute(params)).data;
	};

	/**
	 * 메시지 삭제
	 */
	const deleteMessage = async ({ targetMessageID, channelID }: { targetMessageID: string; channelID: string }) => {
		return (await chatAPI.deleteMessages.execute({ roomMessageId: targetMessageID, roomId: channelID })).data;
	};
	/**
	 * 메시지 수정
	 */
	const updateMessage = async ({
		targetMessageID,
		channelID,
		contents,
		searchMessage
	}: {
		targetMessageID: string;
		channelID: string;
		contents: string;
		searchMessage: string;
	}) => {
		return (
			await chatAPI.updateMessage.execute({
				roomMessageId: targetMessageID,
				roomId: channelID,
				message: contents,
				searchMessage
			})
		).data;
	};

	/**
	 * 채팅 읽음 처리
	 * readMessage는 메시지를 읽어온다는 느낌이라, messageReaded로 변경 from @장진형
	 */
	const messageReaded = async ({ channelID }: { channelID: string; messageID?: string }) => {
		return (await chatAPI.messageReaded.execute({ roomId: channelID })).data;
	};
	/**
	 * 채팅방 공지 등록
	 */
	const registerNotice = async ({ channelID, messageID }: { channelID: string; messageID: string }) => {
		return (await chatAPI.notice.register.execute({ roomId: channelID, messageId: messageID })).data;
	};
	/**
	 * 채팅방 공지 삭제
	 */
	const removeNotice = async ({ channelID }: { channelID: string }) => {
		return (await chatAPI.notice.remove.execute({ roomId: channelID })).data;
	};

	/**
	 * 채팅 리액션 추가
	 */
	const addReaction = async (request: WebServices.Chat.Reaction.Add.Request) => {
		return (await chatAPI.reaction.add.execute(request)).data;
	};
	/**
	 * 채팅 리액션 제거
	 */
	const removeReaction = async (request: WebServices.Chat.Reaction.Remove.Request) => {
		return (await chatAPI.reaction.remove.execute(request)).data as WebServices.Chat.Reaction.Remove.Request;
	};

	const addBookmark = async ({ roomId, messageId }: { roomId: string; messageId: string }) => {
		return (await chatAPI.bookmark.add.execute({ roomId, messageId })).data;
	};

	const removeBookmark = async ({ roomId, messageId }: { roomId: string; messageId: string }) => {
		return (await chatAPI.bookmark.remove.execute({ roomId, messageId })).data;
	};

	/**
	 * 스레드 읽음 처리
	 */
	const threadReaded = async ({ roomId, threadMessageId }: { roomId: string; threadMessageId: string }) => {
		return (await chatAPI.threadReaded.execute({ roomId, threadMessageId })).data;
	};

	return {
		sendMessage,
		deleteMessage,
		updateMessage,
		messageReaded,
		registerNotice,
		removeNotice,
		addReaction,
		removeReaction,
		addBookmark,
		removeBookmark,
		threadReaded
	};
}
